import { Component, ViewChild, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CustomerStatsCardComponent } from '../components/customer-stats-card/customer-stats-card.component';
import { CustomerStatData, CustomerStatisticsApiResponse } from '../shared/types';
import { AddCustomerModalComponent } from '../components/add-customer-modal/add-customer-modal.component';
import { CustomerTableComponent } from '../components/customer-table/customer-table.component';
import { CustomerService, CustomerData } from '../services/customer.service';
import { LocationService } from '../services/location.service';
import { NotificationService } from '../services/notification.service'; // NotificationService;

/**
 * Manages the customer overview UI: displays customer statistics, a customer table, and provides a modal to add customers.
 * @component
 * @selector app-customer
 * @example
 *   <app-customer></app-customer>
 * @input N/A none - This component does not expose @Input properties; it obtains data via services and coordinates child components.
 * @output N/A none - This component does not emit @Output events; interactions are handled via child components and service calls.
 *
 * Key properties:
 *   - isModalOpen: boolean - Controls whether the Add Customer modal is visible.
 *   - pendingCustomerData: CustomerData | null - Temporarily holds customer data being created (used for retry/force-create flows).
 *   - customerStats: CustomerStatData[] - Array of stats displayed by the stats cards.
 *   - addCustomerModal: ViewChild(AddCustomerModalComponent) - Reference to the add customer modal for setting errors/resetting state.
 *   - customerTable: ViewChild(CustomerTableComponent) - Reference to the customer table used to trigger refreshes.
 *
 * Key methods:
 *   - ngOnInit(): Lifecycle hook that initializes the component and calls loadCustomerStatistics().
 *   - loadCustomerStatistics(): Requests statistics from the API, transforms the response for the UI, and falls back to default stats on error (also shows error toast).
 *   - transformApiDataToStats(data: CustomerStatisticsApiResponse): Maps API response fields into CustomerStatData objects used by the stats cards (formats values and percentages).
 *   - setDefaultStats(): Populates customerStats with safe default placeholder values used when the API call fails.
 *   - openModal(): Opens the Add Customer modal (sets isModalOpen = true).
 *   - closeModal(): Closes the modal, resets modal submitting state, and clears duplicate errors.
 *   - onCustomerCreated(customerData: CustomerData): Orchestrates customer creation flow—calls the service, detects duplicates, and delegates to appropriate handlers for success or error.
 *   - isDuplicateCustomerResponse(response: any): Detects duplicate-customer responses returned as success payloads by the API.
 *   - isDuplicateCustomerError(error: any): Detects duplicate-customer errors from failed API responses (e.g., HTTP 409 or messages containing "duplicate" / "already exists").
 *   - handleDuplicateCustomer(response: any, customerData: CustomerData): Surfaces duplicate information in the add-customer modal (shows existing record details and error).
 *   - handleCreationError(error: any): Parses validation and field-level errors returned by the API, sets field errors on the modal when applicable, shows relevant toast messages, and resets modal submitting state.
 *   - handleSuccessfulCreation(customerData: CustomerData): Shows a success toast including a best-effort customer name, refreshes the customer table and statistics, closes the modal, and clears pendingCustomerData.
 *   - getFullName(firstName: string, lastName: string | null): Utility to build a full name string with null-safe behavior.
 *   - getInitials(firstName: string, lastName?: string | null): Utility to compute display initials with sensible fallbacks.
 *
 * Important lifecycle hooks:
 *   - ngOnInit: Invoked on component initialization to load customer statistics.
 */
@Component({
  selector: 'app-customer',
  standalone: true,
  imports: [
    CommonModule,
    CustomerStatsCardComponent,
    AddCustomerModalComponent,
    CustomerTableComponent,
  ],
  templateUrl: './customer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './customer.component.scss',
})
export class CustomerComponent implements OnInit {
  @ViewChild(AddCustomerModalComponent) addCustomerModal!: AddCustomerModalComponent;
  @ViewChild(CustomerTableComponent) customerTable!: CustomerTableComponent;

  isModalOpen = false;
  pendingCustomerData: CustomerData | null = null;

  customerStats: CustomerStatData[] = [];

  constructor(
    private customerService: CustomerService,
    private locationService: LocationService,
    private messageService: NotificationService
  ) {}

  ngOnInit() {
    this.locationService.clearShopLocation();
    this.setDefaultStats();
    this.loadCustomerStatistics();
  }

  /**
   * Load customer statistics, update customerStats with transformed data, and handle errors by showing an error message and falling back to default stats.
   * @example
   * loadCustomerStatistics()
   * void
   * @returns {{void}} Does not return a value; updates component state and handles API errors.
   **/
  private loadCustomerStatistics() {
    this.customerService.getCustomerStatistics().subscribe({
      next: response => {
        if (response.status && response.data) {
          const statsPayload = this.unwrapCustomerStatistics(response.data);
          if (statsPayload) {
            this.customerStats = this.transformApiDataToStats(statsPayload);
          }
        }
      },
      error: error => {
        console.error('Error loading customer statistics:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load customer statistics. Please try again.',
          life: 3000,
        });
        // Fallback to default stats if API fails
        this.setDefaultStats();
      },
    });
  }

  /**
   * Accepts either a flat statistics object or common nested shapes from the API.
   */
  private unwrapCustomerStatistics(data: any): CustomerStatisticsApiResponse | null {
    if (!data || typeof data !== 'object') {
      return null;
    }
    const d = data as Record<string, any>;
    if ('revenue_for_today' in d || 'total_customers_this_month' in d) {
      return d as any as CustomerStatisticsApiResponse;
    }
    const nested = d['statistics'] ?? d['stats'] ?? d['summary'] ?? d['data'];
    if (nested && typeof nested === 'object') {
      return nested as any as CustomerStatisticsApiResponse;
    }
    return null;
  }

  /**
   * Transform raw customer statistics API response into an array of formatted customer stat objects for UI cards.
   * @example
   * transformApiDataToStats({
   *   revenue_for_today: '1500',
   *   revenue_growth_percentage: '12.5',
   *   visits_this_month: '300',
   *   visit_growth_percentage: '-2.3',
   *   total_customers_this_month: '1200',
   *   customer_growth_percentage: '5',
   *   new_customers_this_month: '45',
   *   new_customers_growth_percentage: '10'
   * })
   * [
   *   { title: "Today’s Revenue", value: "$1500", percentage: "+12.5%", periodText: "this month", gradient: "linear-gradient(180deg, #A9DF85 0%, #6DA548 100%)", icon: "assets/customer/c-1.png" },
   *   { title: "Average Visits", value: "300", percentage: "-2.3%", periodText: "this month", gradient: "linear-gradient(180deg, #AFA5F6 0%, #8878F2 100%)", icon: "assets/customer/c-2.png" },
   *   { title: "Total Customers", value: "1200", percentage: "+5%", periodText: "this month", gradient: "linear-gradient(180deg, #A3C7FF 0%, #5186D9 100%)", icon: "assets/customer/c-3.png" },
   *   { title: "New Customers", value: "45", percentage: "+10%", periodText: "this month", gradient: "linear-gradient(180deg, #77D7EA 0%, #42ADC0 100%)", icon: "assets/customer/c-4.png" }
   * ]
   * @param {{CustomerStatisticsApiResponse}} {{data}} - API response object containing raw string statistics fields.
   * @returns {{CustomerStatData[]}} Array of formatted customer statistic objects ready for display in the UI.
   **/
  private transformApiDataToStats(data: CustomerStatisticsApiResponse): CustomerStatData[] {
    const formatValue = (value: string | null): string => {
      return value || '0';
    };

    const formatPercentage = (value: string | null): string => {
      if (!value) return '0%';
      const numValue = parseFloat(value);
      return numValue >= 0 ? `+${value}%` : `${value}%`;
    };

    return [
      {
        title: 'Today’s Revenue',
        value: `$${formatValue(data?.revenue_for_today)}`,
        percentage: formatPercentage(data.revenue_growth_percentage),
        periodText: 'this month',
        gradient: 'linear-gradient(180deg, #A9DF85 0%, #6DA548 100%)',
        icon: 'assets/customer/c-1.png',
      },
      {
        title: 'Average Visits',
        value: formatValue(data.visits_this_month),
        percentage: formatPercentage(data.visit_growth_percentage),
        periodText: 'this month',
        gradient: 'linear-gradient(180deg, #AFA5F6 0%, #8878F2 100%)',
        icon: 'assets/customer/c-2.png',
      },
      {
        title: 'Total Customers',
        value: formatValue(data.total_customers_this_month),
        percentage: formatPercentage(data.customer_growth_percentage),
        periodText: 'this month',
        gradient: 'linear-gradient(180deg, #A3C7FF 0%, #5186D9 100%)',
        icon: 'assets/customer/c-3.png',
      },
      {
        title: 'New Customers',
        value: formatValue(data.new_customers_this_month),
        percentage: formatPercentage(data.new_customers_growth_percentage),
        periodText: 'this month',
        gradient: 'linear-gradient(180deg, #77D7EA 0%, #42ADC0 100%)',
        icon: 'assets/customer/c-4.png',
      },
    ];
  }

  /**
   * Initialize the component's customerStats property with a default set of statistic objects.
   * @example
   * setDefaultStats()
   * undefined
   * @returns {void} Populates this.customerStats with default stat entries (title, value, percentage, periodText, gradient, icon).
   **/
  private setDefaultStats() {
    this.customerStats = [
      {
        title: 'Today’s Revenue',
        value: '$0',
        percentage: '0%',
        periodText: 'this month',
        gradient: 'linear-gradient(180deg, #A9DF85 0%, #6DA548 100%)',
        icon: 'assets/customer/c-1.png',
      },
      {
        title: 'Average Visits',
        value: '0',
        percentage: '0%',
        periodText: 'this month',
        gradient: 'linear-gradient(180deg, #AFA5F6 0%, #8878F2 100%)',
        icon: 'assets/customer/c-2.png',
      },
      {
        title: 'Total Customers',
        value: '0',
        percentage: '0%',
        periodText: 'this month',
        gradient: 'linear-gradient(180deg, #A3C7FF 0%, #5186D9 100%)',
        icon: 'assets/customer/c-3.png',
      },
      {
        title: 'New Customers',
        value: '0',
        percentage: '0%',
        periodText: 'this month',
        gradient: 'linear-gradient(180deg, #77D7EA 0%, #42ADC0 100%)',
        icon: 'assets/customer/c-4.png',
      },
    ];
  }

  openModal() {
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
    // Reset modal form state when closing
    if (this.addCustomerModal) {
      this.addCustomerModal.resetSubmittingState();
      this.addCustomerModal.clearDuplicateCustomerError();
    }
  }

  /**
   * Initiates creation of a customer using the provided customer data and handles success, duplicate detection, and errors.
   * @example
   * nCustomerCreated({ id: '123', name: 'Acme Corp', email: 'contact@acme.com' })
   * void
   * @param {{CustomerData}} {{customerData}} - The customer data object used to attempt creation.
   * @returns {{void}} Performs an asynchronous create operation and handles responses via internal handlers; no direct return value.
   **/
  onCustomerCreated(customerData: CustomerData) {
    this.pendingCustomerData = customerData;

    // First attempt without force_create
    this.customerService.createCustomer(customerData, false).subscribe({
      next: response => {
        // Check if the response indicates a duplicate customer
        if (this.isDuplicateCustomerResponse(response)) {
          this.handleDuplicateCustomer(response, customerData);
        } else {
          this.handleSuccessfulCreation(customerData);
        }
      },
      error: error => {
        console.error('Error creating customer:', error);
        this.handleCreationError(error);
      },
    });
  }

  private isDuplicateCustomerResponse(response: any): boolean {
    // Check if the response message indicates a duplicate customer
    return (
      response &&
      response.message &&
      (response.message.toLowerCase().includes('already exists') ||
        response.message.toLowerCase().includes('duplicate'))
    );
  }

  private isDuplicateCustomerError(error: any): boolean {
    // Check if the error indicates a duplicate customer
    // This will depend on your API's error response format
    return (
      error.status === 409 ||
      (error.error &&
        error.error.message &&
        error.error.message.toLowerCase().includes('duplicate')) ||
      (error.error &&
        error.error.message &&
        error.error.message.toLowerCase().includes('already exists'))
    );
  }

  /**
   * Handle a duplicate-customer API response by extracting an error message and showing it in the add-customer modal.
   * @example
   * this.handleDuplicateCustomer({ message: 'A customer with this information already exists.', data: { id: '123' } }, { name: 'Jane Doe', email: 'jane@example.com' })
   * undefined
   * @param {{any}} {{response}} - Response object from the API which may contain a message and data for the existing customer.
   * @param {{CustomerData}} {{customerData}} - The customer data that was attempted to be added (not used directly for UI but kept for context).
   * @returns {{void}} No return value; the method updates the addCustomerModal with a duplicate-customer error if available.
   */
  private handleDuplicateCustomer(response: any, customerData: CustomerData) {
    // Extract existing customer data from response
    let errorMessage = 'A customer with this information already exists.';

    // Use the message from the API response
    if (response && response.message) {
      errorMessage = response.message;
    }

    // Show error in the add customer modal
    if (this.addCustomerModal) {
      this.addCustomerModal.setDuplicateCustomerError(errorMessage, response.data);
    }
  }

  /**
   * Handle errors from a customer creation request by mapping field-specific validation errors to the addCustomerModal and displaying user-facing messages.
   * @example
   * handleCreationError({ error: { errors: { email: 'Invalid email address' } } })
   * undefined
   * @param {{any}} {{error}} - The error object returned from the server or request.
   * @returns {{void}} Return description in one line.
   **/
  private handleCreationError(error: any) {
    // Check if the error contains field-specific validation errors
    const { error: errBody } = error;
    if (errBody?.errors && typeof errBody.errors === 'object') {
      const errors = errBody.errors as Record<string, string>;
      if (errors['email'] && this.addCustomerModal) {
        this.addCustomerModal.setFieldValidationError('email', errors['email']);
      }
      if ((errors['phone'] || errors['contact']) && this.addCustomerModal) {
        this.addCustomerModal.setFieldValidationError(
          'phone',
          errors['phone'] || errors['contact']
        );
      }
      if ((errors['licensePlate'] || errors['license_plate']) && this.addCustomerModal) {
        this.addCustomerModal.setFieldValidationError(
          'licensePlate',
          errors['licensePlate'] || errors['license_plate']
        );
      }

      // Show general error toast
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fix the errors in the form.',
        life: 3000,
      });
    } else if (errBody?.message) {
      const errorMessage = errBody.message as string;
      const messageLower = errorMessage.toLowerCase();
      let fieldSpecificError = false;
      if (messageLower.includes('email') && this.addCustomerModal) {
        this.addCustomerModal.setFieldValidationError('email', errorMessage);
        fieldSpecificError = true;
      }
      if (
        (messageLower.includes('phone') || messageLower.includes('contact')) &&
        this.addCustomerModal
      ) {
        this.addCustomerModal.setFieldValidationError('phone', errorMessage);
        fieldSpecificError = true;
      }
      if (
        (messageLower.includes('license plate') || messageLower.includes('vehicle')) &&
        this.addCustomerModal
      ) {
        this.addCustomerModal.setFieldValidationError('licensePlate', errorMessage);
        fieldSpecificError = true;
      }

      // Show generic error toast only if no field-specific error was found
      if (!fieldSpecificError) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage,
          life: 3000,
        });
      }
    } else {
      // Show generic error toast
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to create customer. Please try again.',
        life: 3000,
      });
    }

    // Reset the modal's submitting state so user can try again
    if (this.addCustomerModal) {
      this.addCustomerModal.resetSubmittingState();
    }
  }

  /**
   * Notify the user of a successful customer creation, refresh related UI, and clear pending data.
   * @example
   * handleSuccessfulCreation({ firstName: 'Jane', lastName: 'Doe' })
   * undefined
   * @param {{CustomerData}} {{customerData}} - CustomerData object containing customer fields such as firstName and lastName.
   * @returns {{void}} Void; performs side effects (shows success toast, refreshes table and stats, closes modal).
   **/
  private handleSuccessfulCreation(customerData: CustomerData) {
    // Build customer name for toast message, handling null/undefined values
    const firstName = customerData.firstName?.trim() || '';
    const lastName = customerData.lastName?.trim() || '';
    let customerName = '';

    if (firstName && lastName) {
      customerName = `${firstName} ${lastName}`;
    } else if (firstName) {
      customerName = firstName;
    } else if (lastName) {
      customerName = lastName;
    } else {
      customerName = 'Created Customer';
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: `${customerName} is now in your customer list.`,
      life: 3000,
    });

    // Refresh the customer table and statistics
    if (this.customerTable) {
      this.customerTable.refreshTable();
    }
    this.loadCustomerStatistics();

    this.closeModal();
    this.pendingCustomerData = null;
  }

  private getFullName(firstName: string, lastName: string | null): string {
    if (!firstName) return '';
    if (!lastName) return firstName;
    return `${firstName} ${lastName}`;
  }

  /**
   * Return uppercase initials based on first and optional last name, or '??' if none.
   * @example
   * getInitials('John', 'Doe')
   * 'JD'
   * @param {{string}} {{firstName}} - The person's first name (required).
   * @param {{string|null|undefined}} {{lastName}} - The person's last name (optional); if omitted or null, the function uses the first two characters of the firstName.
   * @returns {{string}} The resulting uppercase initials, or '??' if no valid name provided.
   **/
  private getInitials(firstName: string, lastName?: string | null): string {
    let initials = '';

    if (firstName) {
      initials += firstName.charAt(0).toUpperCase();
    }

    if (lastName) {
      initials += lastName.charAt(0).toUpperCase();
    }

    // If we only have firstName and no lastName, take first two characters of firstName
    if (!lastName && firstName.length > 1) {
      initials = firstName.substring(0, 2).toUpperCase();
    }

    return initials || '??';
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
